import React, { useState, useEffect } from 'react';

const TaskForm = ({ task, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    task_name: '',
    task_description: '',
    task_start: '',
    task_end: '',
    task_hours: '',
    task_color: '#0087D4',
    task_status: 'pending',
    task_type: 'task'
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      // Format dates for datetime-local inputs
      const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        task_name: task.title || '',
        task_description: task.description || '',
        task_start: formatDateForInput(task.start),
        task_end: formatDateForInput(task.end),
        task_hours: task.hours || '',
        task_color: task.backgroundColor || '#0087D4',
        task_status: task.status || 'pending',
        task_type: task.type || 'task'
      });
    }
  }, [task]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.task_name.trim()) {
      alert('Task name is required');
      return;
    }

    if (!formData.task_start) {
      alert('Start date is required');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving task:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const typeOptions = [
    { value: 'task', label: 'Task' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'event', label: 'Event' },
    { value: 'reminder', label: 'Reminder' }
  ];

  const colorOptions = [
    { value: '#0087D4', label: 'Blue' },
    { value: '#28a745', label: 'Green' },
    { value: '#ffc107', label: 'Yellow' },
    { value: '#dc3545', label: 'Red' },
    { value: '#6f42c1', label: 'Purple' },
    { value: '#fd7e14', label: 'Orange' },
    { value: '#20c997', label: 'Teal' },
    { value: '#6c757d', label: 'Gray' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              {task ? 'Edit Task' : 'Create New Task'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Task Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Task Name *
              </label>
              <input
                type="text"
                name="task_name"
                value={formData.task_name}
                onChange={handleChange}
                className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                placeholder="Enter task name"
                required
              />
            </div>

            {/* Task Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Description
              </label>
              <textarea
                name="task_description"
                value={formData.task_description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
                placeholder="Enter task description"
              />
            </div>

            {/* Start and End Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  name="task_start"
                  value={formData.task_start}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  name="task_end"
                  value={formData.task_end}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Hours, Status, and Type */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Hours
                </label>
                <input
                  type="number"
                  name="task_hours"
                  value={formData.task_hours}
                  onChange={handleChange}
                  min="0"
                  step="0.5"
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Status
                </label>
                <select
                  name="task_status"
                  value={formData.task_status}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Type
                </label>
                <select
                  name="task_type"
                  value={formData.task_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {typeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Color
              </label>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {colorOptions.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, task_color: color.value }))}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      formData.task_color === color.value 
                        ? 'border-gray-900 scale-110' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md shadow-sm transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center px-4 py-2 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200 disabled:opacity-50"
                style={{ 
                  backgroundColor: '#0087D4', 
                  boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                }}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  task ? 'Update Task' : 'Create Task'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TaskForm;